<script setup lang="ts">
/**
 * Text whose digits keep one width, so a ticking clock or a live counter does
 * not jitter („1:11“ is as wide as „8:88“).
 *
 * The app font (Basier Circle) has no tabular figures — `font-variant-numeric:
 * tabular-nums` does nothing with it — so each digit sits in a box as wide as
 * the widest one, centred like a real tabular figure. Everything else (colons,
 * spaces, letters) keeps its own width. The spans are purely visual: the text
 * reads and copies as one string.
 */
import { computed } from 'vue';
import { splitDigits } from './digits';

const props = defineProps<{
  /** The text or number to render. */
  value: string | number | null | undefined;
}>();

const parts = computed(() => splitDigits(props.value == null ? '' : String(props.value)));
</script>

<template>
  <span class="nx-tabular-number">
    <template v-for="(part, i) in parts" :key="i">
      <span v-if="part.digit" class="nx-tabular-number__digit">{{ part.text }}</span>
      <template v-else>{{ part.text }}</template>
    </template>
  </span>
</template>

<style scoped>
.nx-tabular-number {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
/* 0.63em is the advance of the widest digit („0“) in Basier Circle; a font
   with real tabular figures fits the box as well. */
.nx-tabular-number__digit {
  display: inline-block;
  width: 0.63em;
  text-align: center;
}
</style>
